import React from 'react';
import { motion } from 'framer-motion';
import {
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  AcademicCapIcon,
  UserIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';

const TrainingSessionCard = ({ session, onView, onEdit, onDelete }) => {
  // Formatage des dates et heures
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };
  
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };
  
  // Calcul de la durée
  const getDuration = () => {
    if (!session.startDateTime || !session.endDateTime) return '';
    
    const start = new Date(session.startDateTime);
    const end = new Date(session.endDateTime);
    const durationMs = end - start;
    
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`;
  };
  
  // Calculer le nombre de jours restants
  const getDaysRemaining = () => {
    if (!session.startDateTime) return '';
    
    const start = new Date(session.startDateTime);
    const now = new Date();
    const daysRemaining = Math.ceil((start - now) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining < 0) {
      return "Terminé";
    } else if (daysRemaining === 0) {
      return "Aujourd'hui";
    } else if (daysRemaining === 1) {
      return "Demain";
    } else {
      return `Dans ${daysRemaining} jours`;
    }
  };
  
  // Style basé sur le temps restant
  const getTimeRemainingStyle = () => {
    if (!session.startDateTime) return '';
    
    const start = new Date(session.startDateTime);
    const now = new Date();
    const daysRemaining = Math.ceil((start - now) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining < 0) {
      return "text-gray-400";
    } else if (daysRemaining <= 2) {
      return "text-red-400";
    } else if (daysRemaining <= 7) {
      return "text-amber-400";
    } else {
      return "text-blue-400";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-5"
    >
      {/* En-tête avec titre et module */}
      <div className="mb-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">
              {session.title}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 flex items-center mt-1">
              <AcademicCapIcon className="h-4 w-4 mr-2" />
              {session.moduleName}
            </p>
          </div>
          
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getTimeRemainingStyle()}`}>
            {getDaysRemaining()}
          </div>
        </div>
      </div>
      
      {/* Informations principales */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center">
          <CalendarDaysIcon className="h-5 w-5 text-blue-400 mr-2" />
          <div>
            <p className="text-slate-500 dark:text-slate-400 text-xs">Date</p>
            <p className="text-slate-900 dark:text-slate-50">
              {formatDate(session.startDateTime)} {session.endDateTime && session.startDateTime?.split('T')[0] !== session.endDateTime?.split('T')[0] && ` - ${formatDate(session.endDateTime)}`}
            </p>
          </div>
        </div>
        
        <div className="flex items-center">
          <ClockIcon className="h-5 w-5 text-green-400 mr-2" />
          <div>
            <p className="text-slate-500 dark:text-slate-400 text-xs">Horaire</p>
            <p className="text-slate-900 dark:text-slate-50">
              {formatTime(session.startDateTime)} - {formatTime(session.endDateTime)}
            </p>
          </div>
        </div>
        
        <div className="flex items-center">
          <MapPinIcon className="h-5 w-5 text-red-400 mr-2" />
          <div>
            <p className="text-slate-500 dark:text-slate-400 text-xs">Lieu</p>
            <p className="text-slate-900 dark:text-slate-50">{session.location}</p>
          </div>
        </div>
        
        <div className="flex items-center">
          <UserIcon className="h-5 w-5 text-purple-400 mr-2" />
          <div>
            <p className="text-slate-500 dark:text-slate-400 text-xs">Formateur</p>
            <p className="text-slate-900 dark:text-slate-50">{session.instructorName}</p>
          </div>
        </div>
      </div>
      
      {/* Informations secondaires */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-2">
          <p className="text-slate-500 dark:text-slate-400 text-xs">Durée</p>
          <p className="text-slate-900 dark:text-slate-50 font-medium">{getDuration()}</p>
        </div>
        
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-2">
          <p className="text-slate-500 dark:text-slate-400 text-xs">Places</p>
          <p className="text-slate-900 dark:text-slate-50 font-medium">
            {session.enrolledStudents?.length || 0} / {session.capacity || '-'}
          </p>
        </div>
        
        {session.price && (
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-2">
            <p className="text-slate-500 dark:text-slate-400 text-xs">Prix</p>
            <p className="text-slate-900 dark:text-slate-50 font-medium">{session.price} DA</p>
          </div>
        )}
      </div>
      
      {/* Description */}
      {session.description && (
        <div className="mb-4">
          <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-2">{session.description}</p>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex justify-end space-x-2 mt-2">
        <button
          onClick={() => onView(session)}
          className="bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-900 dark:text-slate-50 p-2 rounded-lg transition-colors"
          title="Voir les détails"
        >
          <DocumentDuplicateIcon className="h-5 w-5" />
        </button>
        
        <button
          onClick={() => onEdit(session)}
          className="bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-900 dark:text-slate-50 p-2 rounded-lg transition-colors"
          title="Modifier"
        >
          <PencilSquareIcon className="h-5 w-5" />
        </button>
        
        <button
          onClick={() => onDelete(session.id)}
          className="bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-900 dark:text-slate-50 p-2 rounded-lg transition-colors"
          title="Supprimer"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>
    </motion.div>
  );
};

export default TrainingSessionCard;